import { sendMessage } from "./chatService";
import { Artifact, ArtifactProgress } from "../types/chat";
import { emitSessionUpdated } from "../utils/chatEvents";

type StreamEvent =
  | { type: "snapshot"; content: string; done: boolean }
  | { type: "chunk"; content: string }
  | { type: "done"; messageId?: number; artifact?: Artifact }
  | { type: "artifact_progress"; progress: ArtifactProgress }
  | { type: "error"; error: string; tokenLimitError?: {
      error?: string;
      message?: string;
      tokens_used?: number;
      token_limit?: number;
      estimated_usage?: number;
      tokens_remaining?: number;
      upgrade_url?: string;
    } };

type StreamListener = (event: StreamEvent) => void;

interface SessionStreamState {
  buffer: string;
  done: boolean;
  subscribers: Set<StreamListener>;
  reading: boolean;
  // Track last message id reported by backend (optional)
  lastMessageId?: number;
}
// TODO: add a cancel button that cancels the message
// TODO: make sure the user can't send a message in the same chat session while the stream is running, but he can send into other sessions/new chats
// TODO: add retry button that retries the message
class StreamManager {
  private sessionStreams: Map<number, SessionStreamState> = new Map();

  getSnapshot(sessionId: number): { content: string; done: boolean } {
    const state = this.sessionStreams.get(sessionId);
    return {
      content: state?.buffer ?? "",
      done: state?.done ?? false,
    };
  }

  isReading(sessionId: number): boolean {
    const state = this.sessionStreams.get(sessionId);
    return Boolean(state?.reading);
  }

  subscribe(sessionId: number, listener: StreamListener): () => void {
    let state = this.sessionStreams.get(sessionId);
    if (!state) {
      state = {
        buffer: "",
        done: false,
        subscribers: new Set(),
        reading: false,
      };
      this.sessionStreams.set(sessionId, state);
    }

    state.subscribers.add(listener);

    // Immediately send current snapshot
    listener({ type: "snapshot", content: state.buffer, done: state.done });

    return () => {
      const current = this.sessionStreams.get(sessionId);
      if (current) {
        current.subscribers.delete(listener);
        // If no subscribers and stream is done, we can optionally cleanup
        if (current.subscribers.size === 0 && current.done) {
          this.sessionStreams.delete(sessionId);
        }
      }
    };
  }

  async startStream(
    sessionId: number,
    message: string,
    files?: File[],
  ): Promise<void> {
    let state = this.sessionStreams.get(sessionId);
    if (!state) {
      state = {
        buffer: "",
        done: false,
        subscribers: new Set(),
        reading: false,
      };
      this.sessionStreams.set(sessionId, state);
    }

    // If already reading for this session, do not start another (single in-flight per session)
    if (state.reading) {
      return;
    }

    state.reading = true;

    try {
      const response = await sendMessage(sessionId, message, files);
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        state.reading = false;
        // Keep state, but notify error
        this.emit(sessionId, {
          type: "error",
          error: "Falha ao iniciar streaming",
        });
        return;
      }

      try {
        let receivedDone = false;
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(5));

                // Check for artifact progress event
                if (data && data.artifact_progress) {
                  this.emit(sessionId, {
                    type: "artifact_progress",
                    progress: data.artifact_progress,
                  });
                }

                if (
                  data &&
                  data.done === false &&
                  typeof data.content === "string"
                ) {
                  state.buffer += data.content;
                  this.emit(sessionId, {
                    type: "chunk",
                    content: data.content,
                  });
                } else if (data && data.done === true) {
                  receivedDone = true;
                  state.done = true;
                  if (typeof data.message_id === "number") {
                    state.lastMessageId = data.message_id;
                  }

                  // Check if this is a token limit error
                  if (data.error && data.metadata &&
                      (data.metadata.error === "token_limit_exceeded" ||
                       data.metadata.error === "token_limit_would_exceed")) {
                    this.emit(sessionId, {
                      type: "error",
                      error: data.metadata.message || data.error,
                      tokenLimitError: data.metadata,
                    });
                  } else if (data.error) {
                    // Qualquer outra falha reportada pelo backend. Antes caía
                    // no ramo "done" abaixo e a bolha da IA ficava vazia com o
                    // indicador de "pensando" girando indefinidamente.
                    this.emit(sessionId, {
                      type: "error",
                      error:
                        typeof data.error === "string" && data.error
                          ? data.error
                          : "Falha ao gerar resposta",
                    });
                  } else {
                    this.emit(sessionId, {
                      type: "done",
                      messageId: state.lastMessageId,
                      artifact: data.artifact,
                    });
                    // Notify sidebar to refresh after delay
                    // Title generation now starts at the BEGINNING of streaming (in parallel)
                    // So it should be ready ~2-3s after the question was sent
                    setTimeout(() => {
                      emitSessionUpdated(sessionId);
                    }, 2500);
                  }
                }
              } catch {
                // Ignore parse errors for non-JSON lines
              }
            }
          }
        }

        // If stream ended without receiving "done" event, it was interrupted.
        // Vale também para buffer vazio: sem este evento a bolha da IA ficaria
        // em branco com o indicador de "pensando" para sempre.
        if (!receivedDone) {
          this.emit(sessionId, {
            type: "error",
            error: "Conexão interrompida durante o streaming",
          });
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      // Check if this is a token limit error
      const tokenLimitError = (error as Error & { tokenLimitError?: unknown })?.tokenLimitError;

      this.emit(sessionId, {
        type: "error",
        error: error instanceof Error ? error.message : "Erro de streaming",
        tokenLimitError: tokenLimitError as {
          error?: string;
          message?: string;
          tokens_used?: number;
          token_limit?: number;
          estimated_usage?: number;
          tokens_remaining?: number;
          upgrade_url?: string;
        } | undefined,
      });
    } finally {
      state.reading = false;
      // Keep state so buffer is available when user returns
    }
  }

  private emit(sessionId: number, event: StreamEvent) {
    const state = this.sessionStreams.get(sessionId);
    if (!state) return;
    state.subscribers.forEach((cb) => {
      try {
        cb(event);
      } catch {
        /* noop */
      }
    });
  }
}

export const streamManager = new StreamManager();
